import React, { useState, useEffect } from 'react';
import Button from 'aldoo-ra/Button';
import Input from 'aldoo-ra/Input';
import Checkbox from 'aldoo-ra/Checkbox';
import DropdownSelect from 'aldoo-ra/DropdownSelect';
import Menu from 'aldoo-ra/Menu';
import DataTable from 'aldoo-ra/DataTable';
import AldooImage from 'aldoo-ra/Image';
import Panel from 'aldoo-ra/Panel';
import ProgressBar from 'aldoo-ra/ProgressBar';
import Spinner from 'aldoo-ra/Spinner';
import Tabs from 'aldoo-ra/Tabs';
import ToggleSwitch from 'aldoo-ra/ToggleSwitch';
import Typography from 'aldoo-ra/Typography';
import DatePicker from 'aldoo-ra/DatePicker';
import DateFilterDropdown from 'aldoo-ra/DateFilterDropdown';
import DropZone from 'aldoo-ra/DropZone';
import { GetCMSContent } from 'aldoo-ra/CMS/api';

// ─── Aldoo Button ────────────────────────────────────────────────────────────

export const AldooButtonBehavior = {
  id: 'AldooButton',
  label: 'Button',
  icon: 'Button',
  description: 'Aldoo-RA Button component with full variant support.',
  fields: [
    { name: 'children', label: 'Label', type: 'text', default: 'Button' },
    {
      name: 'variant', label: 'Variant', type: 'select', default: 'admin_primary',
      options: ['admin_primary', 'admin_secondary', 'admin_primary_red', 'admin_error',
        'admin_icon', 'admin_store', 'admin_viewer_controls', 'admin_viewer_controls_small',
        'admin_text_warning', 'admin_user_settings', 'admin_social_connect']
    },
    { name: 'disabled', label: 'Disabled', type: 'boolean', default: false },
    { name: 'loading', label: 'Loading', type: 'boolean', default: false },
  ],
  renderContent: (props) =>
    React.createElement(Button, {
      variant: props.variant || 'admin_primary',
      disabled: props.disabled,
      loading: props.loading,
      className: 'w-full h-full',
    }, props.children || 'Button'),
};

// ─── Aldoo Input ─────────────────────────────────────────────────────────────

export const AldooInputBehavior = {
  id: 'AldooInput',
  label: 'Input',
  icon: 'Input',
  description: 'Aldoo-RA Input component with full variant support.',
  fields: [
    { name: 'label', label: 'Label', type: 'text', default: '' },
    { name: 'placeholder', label: 'Placeholder', type: 'text', default: 'Enter text...' },
    {
      name: 'variant', label: 'Variant', type: 'select', default: 'default',
      options: ['default', 'admin', 'compact', 'admin_compact', 'large', 'admin_large',
        'floating', 'admin_floating', 'borderless', 'admin_borderless', 'admin_preview']
    },
    { name: 'type', label: 'Type', type: 'select', default: 'text', options: ['text', 'password', 'email', 'number', 'search'] },
    { name: 'disabled', label: 'Disabled', type: 'boolean', default: false },
  ],
  renderContent: (props) =>
    React.createElement(Input, {
      variant: props.variant || 'default',
      label: props.label,
      placeholder: props.placeholder || 'Enter text...',
      type: props.type || 'text',
      disabled: props.disabled,
      className: 'w-full',
    }),
};

// ─── Aldoo Checkbox ──────────────────────────────────────────────────────────

export const AldooCheckboxBehavior = {
  id: 'AldooCheckbox',
  label: 'Checkbox',
  icon: 'Check',
  description: 'Aldoo-RA Checkbox component with full variant support.',
  fields: [
    { name: 'label', label: 'Label', type: 'text', default: 'Option' },
    {
      name: 'variant', label: 'Variant', type: 'select', default: 'default',
      options: ['default', 'admin', 'compact', 'admin_compact', 'large', 'admin_large']
    },
    { name: 'checked', label: 'Checked', type: 'boolean', default: false },
    { name: 'labelPosition', label: 'Label Position', type: 'select', default: 'right', options: ['left', 'right'] },
  ],
  renderContent: (props, isLiveMode) =>
    React.createElement('div', { style: { pointerEvents: isLiveMode ? 'auto' : 'none' } },
      React.createElement(Checkbox, {
        variant: props.variant || 'default',
        label: props.label || 'Option',
        checked: props.checked,
        labelPosition: props.labelPosition || 'right',
        onChange: () => {},
      })
    ),
};

// ─── Aldoo DropdownSelect ────────────────────────────────────────────────────

export const AldooDropdownSelectBehavior = {
  id: 'AldooDropdownSelect',
  label: 'Dropdown Select',
  icon: 'ChevronDown',
  description: 'Aldoo-RA DropdownSelect component with full variant support.',
  fields: [
    { name: 'placeholder', label: 'Placeholder', type: 'text', default: 'Select...' },
    {
      name: 'variant', label: 'Variant', type: 'select', default: 'default',
      options: ['default', 'admin', 'inline', 'admin_highlighted']
    },
  ],
  renderContent: (props, isLiveMode) =>
    React.createElement('div', { style: { pointerEvents: isLiveMode ? 'auto' : 'none' } },
      React.createElement(DropdownSelect, {
        variant: props.variant || 'default',
        placeholder: props.placeholder || 'Select...',
        options: [],
        value: null,
        onChange: () => {},
        className: 'w-full',
      })
    ),
};

// ─── Aldoo Menu ──────────────────────────────────────────────────────────────

const MENU_PLACEHOLDER = [
  { label: 'Item 1', link: '#' },
  { label: 'Item 2', link: '#' },
  { label: 'Item 3', link: '#' },
];

const parseMenuConfig = (raw) => {
  if (!raw) return { source: 'custom', menuName: '', items: [] };
  if (typeof raw === 'string') {
    try { return JSON.parse(raw); } catch { return { source: 'custom', menuName: '', items: [] }; }
  }
  return raw;
};

const AldooMenuRenderer = ({ renderAs, menuConfig }) => {
  const config = parseMenuConfig(menuConfig);
  const [cmsMenuData, setCmsMenuData] = useState(null);

  useEffect(() => {
    if (config.source !== 'cms' || !config.menuName) {
      setCmsMenuData(null);
      return;
    }
    GetCMSContent({
      match: { 'content.type': 'MenuItem', 'content.name': config.menuName },
      itemsPerPage: 1,
    })
      .then((result) => {
        const content = result?.data?.[0]?.content;
        if (content) setCmsMenuData(content);
      })
      .catch(() => {});
  }, [config.source, config.menuName]);

  let menuData;
  if (config.source === 'cms' && cmsMenuData) {
    menuData = cmsMenuData;
  } else if (config.source === 'custom' && config.items?.length) {
    menuData = { label: 'Menu', items: config.items };
  } else {
    menuData = { label: 'Menu', items: MENU_PLACEHOLDER };
  }

  return React.createElement(Menu, {
    renderAs: renderAs || 'hlist',
    skipRootItem: true,
    menuData,
  });
};

export const AldooMenuBehavior = {
  id: 'AldooMenu',
  label: 'Menu',
  icon: 'Grid',
  description: 'Aldoo-RA Menu component with full variant support.',
  fields: [
    {
      name: 'renderAs', label: 'Layout', type: 'select', default: 'hlist',
      options: ['hlist', 'hlist.icons', 'vlist', 'vlist.menu', 'panel'],
    },
    {
      name: 'menuConfig', label: 'Menu', type: 'menu_config', default: null,
    },
  ],
  renderContent: (props) => React.createElement(AldooMenuRenderer, props),
};

// ─── Aldoo DataTable ─────────────────────────────────────────────────────────

export const AldooDataTableBehavior = {
  id: 'AldooDataTable',
  label: 'Data Table',
  icon: 'HorizontalLayout',
  description: 'Aldoo-RA DataTable component.',
  fields: [
    {
      name: 'variant', label: 'Variant', type: 'select', default: 'default',
      options: ['default', 'admin']
    },
  ],
  renderContent: (props) =>
    React.createElement(DataTable, {
      variant: props.variant || 'default',
      showSearch: false,
      showFooter: false,
      renders: [
        { id: 'name', title: 'Name', render: (v) => v },
        { id: 'value', title: 'Value', render: (v) => v },
      ],
      data: [
        { name: 'Row 1', value: 'Value A' },
        { name: 'Row 2', value: 'Value B' },
      ],
    }),
};

// ─── Aldoo Image ─────────────────────────────────────────────────────────────

export const AldooImageBehavior = {
  id: 'AldooImage',
  label: 'Image',
  icon: 'Image',
  description: 'Aldoo-RA Image component — pick from media library or load by CMS ID.',
  fields: [
    { name: 'src', label: 'Image', type: 'media_picker', default: '' },
    { name: 'alt', label: 'Alt Text', type: 'text', default: 'image' },
    { name: 'id', label: 'CMS Image ID (optional)', type: 'text', default: '' },
    {
      name: 'variant', label: 'Size Variant', type: 'select', default: 'md',
      options: ['xs', 'sm', 'md', 'lg', 'xl', 'full'],
    },
  ],
  renderContent: (props, isLiveMode) => {
    if (props.src) {
      return React.createElement('img', {
        src: props.src,
        alt: props.alt || 'image',
        className: 'w-full h-full object-contain',
        draggable: false,
        onDragStart: (e) => e.preventDefault(),
      });
    }
    if (props.id) {
      return React.createElement('div', { className: 'w-full h-full', style: { pointerEvents: isLiveMode ? 'auto' : 'none' } },
        React.createElement(AldooImage, {
          id: props.id,
          variant: props.variant || 'md',
          alt: props.alt || 'image',
          className: 'w-full h-full object-contain',
        })
      );
    }
    return React.createElement('div', {
      className: 'w-full h-full flex items-center justify-center bg-slate-100 border border-dashed border-slate-300 rounded text-slate-400 text-xs',
    }, 'Choose an image');
  },
};

// ─── Aldoo Panel ─────────────────────────────────────────────────────────────

export const AldooPanelBehavior = {
  id: 'AldooPanel',
  label: 'Panel',
  icon: 'Container',
  description: 'Aldoo-RA Panel component with collapsible support.',
  fields: [
    { name: 'title', label: 'Title', type: 'text', default: 'Panel' },
    {
      name: 'variant', label: 'Variant', type: 'select', default: 'default',
      options: ['default', 'admin', 'compact', 'admin_compact'],
    },
    { name: 'collapsible', label: 'Collapsible', type: 'boolean', default: true },
    { name: 'defaultOpen', label: 'Default Open', type: 'boolean', default: true },
  ],
  renderContent: (props) =>
    React.createElement(Panel, {
      variant: props.variant || 'default',
      title: props.title || 'Panel',
      collapsible: props.collapsible !== false,
      defaultOpen: props.defaultOpen !== false,
    }, React.createElement('div', { className: 'p-2 text-xs text-slate-400' }, 'Panel content')),
};

// ─── Aldoo ProgressBar ───────────────────────────────────────────────────────

export const AldooProgressBarBehavior = {
  id: 'AldooProgressBar',
  label: 'Progress Bar',
  icon: 'HorizontalLayout',
  description: 'Aldoo-RA ProgressBar component.',
  fields: [
    { name: 'progress', label: 'Progress (0–1)', type: 'number', default: 0.5, step: 0.01 },
    { name: 'title', label: 'Label', type: 'text', default: '' },
    {
      name: 'variant', label: 'Variant', type: 'select', default: 'default',
      options: ['default', 'admin', 'compact', 'success', 'error', 'warning'],
    },
  ],
  renderContent: (props) =>
    React.createElement(ProgressBar, {
      variant: props.variant || 'default',
      progress: props.progress ?? 0.5,
      title: props.title,
    }),
};

// ─── Aldoo Spinner ───────────────────────────────────────────────────────────

export const AldooSpinnerBehavior = {
  id: 'AldooSpinner',
  label: 'Spinner',
  icon: 'Play',
  description: 'Aldoo-RA Spinner loading indicator.',
  fields: [
    {
      name: 'variant', label: 'Variant', type: 'select', default: 'primary',
      options: ['primary', 'secondary', 'admin', 'white'],
    },
    {
      name: 'size', label: 'Size', type: 'select', default: 'md',
      options: ['xs', 'sm', 'md', 'lg', 'xl'],
    },
    {
      name: 'speed', label: 'Speed', type: 'select', default: 'normal',
      options: ['slow', 'normal', 'fast'],
    },
  ],
  renderContent: (props) =>
    React.createElement(Spinner, {
      variant: props.variant || 'primary',
      size: props.size || 'md',
      speed: props.speed || 'normal',
    }),
};

// ─── Aldoo Tabs ──────────────────────────────────────────────────────────────

export const AldooTabsBehavior = {
  id: 'AldooTabs',
  label: 'Tabs',
  icon: 'Grid',
  description: 'Aldoo-RA Tabs component.',
  fields: [
    {
      name: 'variant', label: 'Variant', type: 'select', default: 'default',
      options: ['default', 'admin', 'pills', 'underline', 'admin_underline'],
    },
    {
      name: 'orientation', label: 'Orientation', type: 'select', default: 'horizontal',
      options: ['horizontal', 'vertical'],
    },
    {
      name: 'size', label: 'Size', type: 'select', default: 'medium',
      options: ['small', 'medium', 'large'],
    },
  ],
  renderContent: (props, isLiveMode) =>
    React.createElement('div', { style: { pointerEvents: isLiveMode ? 'auto' : 'none' } },
      React.createElement(Tabs, {
        variant: props.variant || 'default',
        orientation: props.orientation || 'horizontal',
        size: props.size || 'medium',
        tabs: [
          { value: 'tab1', label: 'Tab 1' },
          { value: 'tab2', label: 'Tab 2' },
          { value: 'tab3', label: 'Tab 3' },
        ],
        value: 'tab1',
        onChange: () => {},
      })
    ),
};

// ─── Aldoo ToggleSwitch ──────────────────────────────────────────────────────

export const AldooToggleSwitchBehavior = {
  id: 'AldooToggleSwitch',
  label: 'Toggle Switch',
  icon: 'Check',
  description: 'Aldoo-RA ToggleSwitch component.',
  fields: [
    {
      name: 'variant', label: 'Variant', type: 'select', default: 'default',
      options: ['default', 'admin', 'scrito'],
    },
    { name: 'isOn', label: 'Default On', type: 'boolean', default: false },
  ],
  renderContent: (props, isLiveMode) =>
    React.createElement('div', { style: { pointerEvents: isLiveMode ? 'auto' : 'none' } },
      React.createElement(ToggleSwitch, {
        variant: props.variant || 'default',
        isOn: props.isOn || false,
        onChange: () => {},
      })
    ),
};

// ─── Aldoo Typography ────────────────────────────────────────────────────────

export const AldooTypographyBehavior = {
  id: 'AldooTypography',
  label: 'Typography',
  icon: 'Typography',
  description: 'Aldoo-RA Typography component with semantic text variants.',
  fields: [
    { name: 'children', label: 'Text', type: 'text', default: 'Text content' },
    {
      name: 'variant', label: 'Variant', type: 'select', default: 'txt',
      options: [
        'h1', 'h2', 'h3', 'h4',
        'body', 'body_bold', 'body_sm', 'body_sm_bold',
        'txt', 'txt_bold', 'txt_sm', 'txt_sm_bold',
        'btn', 'btn_sm',
        'admin_h1', 'admin_h2', 'admin_h3', 'admin_label', 'admin_txt', 'admin_txt_sm',
      ],
    },
  ],
  renderContent: (props) =>
    React.createElement(Typography, {
      variant: props.variant || 'txt',
    }, props.children || 'Text content'),
};

// ─── Aldoo DatePicker ────────────────────────────────────────────────────────

export const AldooDatePickerBehavior = {
  id: 'AldooDatePicker',
  label: 'Date Picker',
  icon: 'Database',
  description: 'Aldoo-RA DatePicker component.',
  fields: [
    {
      name: 'variant', label: 'Variant', type: 'select', default: 'default',
      options: ['default', 'admin', 'compact', 'admin_compact'],
    },
  ],
  renderContent: (props, isLiveMode) =>
    React.createElement('div', { style: { pointerEvents: isLiveMode ? 'auto' : 'none' } },
      React.createElement(DatePicker, {
        variant: props.variant || 'default',
        value: null,
        onChange: () => {},
      })
    ),
};

// ─── Aldoo DateFilterDropdown ────────────────────────────────────────────────

export const AldooDateFilterDropdownBehavior = {
  id: 'AldooDateFilterDropdown',
  label: 'Date Range',
  icon: 'Database',
  description: 'Aldoo-RA DateFilterDropdown with preset ranges.',
  fields: [
    { name: 'label', label: 'Label', type: 'text', default: 'Date Range' },
    { name: 'showLabel', label: 'Show Label', type: 'boolean', default: true },
    {
      name: 'variant', label: 'Variant', type: 'select', default: 'default',
      options: ['default', 'admin', 'compact'],
    },
  ],
  renderContent: (props, isLiveMode) =>
    React.createElement('div', { style: { pointerEvents: isLiveMode ? 'auto' : 'none' } },
      React.createElement(DateFilterDropdown, {
        variant: props.variant || 'default',
        label: props.label || 'Date Range',
        showLabel: props.showLabel !== false,
        onChange: () => {},
      })
    ),
};

// ─── Aldoo DropZone ──────────────────────────────────────────────────────────

export const AldooDropZoneBehavior = {
  id: 'AldooDropZone',
  label: 'Drop Zone',
  icon: 'Image',
  description: 'Aldoo-RA DropZone file upload component.',
  fields: [
    { name: 'maxFiles', label: 'Max Files', type: 'number', default: 1 },
    { name: 'accept', label: 'Accept', type: 'text', default: '' },
  ],
  renderContent: (props) =>
    React.createElement(DropZone, {
      maxFiles: props.maxFiles || 1,
      accept: props.accept || undefined,
      onDrop: () => {},
    }),
};
